/**
 * The References section of the Metal Resources landing pages: links to the FAQ and the glossary.
 * Desktop reuses the Careers parallax card block
 * (`src/components/Careers/IgniteYourCareer/IgniteYourCareer.tsx`) with offsets tuned for this
 * section; mobile shows the description and one button link per card, two to a row.
 *
 * Used by `../MetalResourcesLandingRyerson.tsx` and `../MetalResourcesLandingSTS.tsx`, which hold
 * the copy (`ReferencesContent`). Unlike the landing's other sections it takes the copy for every
 * language and picks the visitor's language itself.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import IgniteYourCareer, {
	IgniteYourCareerContent,
	IgniteYourCareerCard,
} from "@components/Careers/IgniteYourCareer/IgniteYourCareer";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { Media } from "@ryerson/frontend.next";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ContentSection } from "@ryerson/frontend.layout";

/** Props for {@link ReferencesSection}. */
export interface ReferencesSectionProps {
	/**
	 * The section copy for all three languages. Mobile uses `description` and each card's `title`
	 * and `actionUrl`; desktop passes the whole entry to `IgniteYourCareer`.
	 */
	content: LanguageContent<IgniteYourCareerContent>;
}

const ButtonLinkContainer = styled.div`
	display: block;
	width: 100%;
	margin-bottom: 82px;
	height: auto;
`;

const ButtonLinkInline = styled.div`
	display: inline-block;
	width: 50%;
	height: auto;
	vertical-align: top;
`;

/** Renders the References section in the visitor's language. */
const ReferencesSection: React.FC<ReferencesSectionProps> = ({ content }) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<IgniteYourCareer
					content={content[language]}
					type="tertiary"
					title1Left="-135px"
					title2Left="-135px"
					title1LeftSmaller="-40px"
					title2LeftSmaller="-28px"
					title1Parallax={["-105px", "-30px"]}
					title2Parallax={["-100px", "-165px"]}
					title1ParallaxSmaller={["-65px", "10px"]}
					title2ParallaxSmaller={["-55px", "-130px"]}
				/>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="0px" hPadding="0px">
					<Typography
						variant="div"
						size="md"
						type="primary"
						css={css`
							margin-top: 24px;
							margin-bottom: 40px;
						`}
					>
						{content[language].description ?? ""}
					</Typography>
					<ButtonLinkContainer>
						{content[language].cards.map(
							(card: IgniteYourCareerCard, index: number) => {
								return (
									<ButtonLinkInline key={index}>
										<ButtonLink
											size="sm"
											orientation="left"
											label={card.title ?? ""}
											to={card.actionUrl ?? ""}
										/>
									</ButtonLinkInline>
								);
							}
						)}
					</ButtonLinkContainer>
				</ContentSection>
			</Media>
		</>
	);
};

export default ReferencesSection;
